import { useDocumentVisibilityEvent } from '@fabric-app/hooks/useDocumentVisibilityEvent';
import { useAuthStatus } from '@fabric-app/compat';
import { captureException } from '@fabric-app/compat/capabilities/sentry';
import { getSocketOrigin, SOCKET_INSTACE_OPTIONS } from './config.js';
import { useRef, useState, useEffect } from 'react';
import { io } from 'socket.io-client';
import { createLogger } from '@fabric-app/utils/logging';
import { getCompatEnv } from '@fabric-app/compat/capabilities/environment';
import { useContextAccessToken } from '../permission/contexts/AnonShareTokenContext.js';
import { useWindowEvent } from '@fabric-app/hooks/useWindowEvent';

const useFabricSocketIo = (options) => {
  const roomPath = options?.roomPath ?? "";
  const enabled = options?.enabled ?? true;
  const accessToken = useContextAccessToken();
  const allowUnauthenticated = accessToken ? true : options?.allowUnauthenticated ?? false;
  const stayConnectedWhenDocumentNotVisible = options?.stayConnectedWhenDocumentNotVisible ?? false;
  const authStatus = useAuthStatus();
  const authStatusRef = useRef(authStatus);
  authStatusRef.current = authStatus;
  const { isDocumentVisible } = useDocumentVisibilityEvent();
  const [state, setState] = useState({
    isConnected: false,
    error: null,
    connectErrorCount: 0
  });
  const [socket] = useState(() => {
    const socketInstance = io(getSocketOrigin() + roomPath, {
      ...SOCKET_INSTACE_OPTIONS,
      ...accessToken ? {
        accessToken
      } : {}
    });
    return socketInstance;
  });
  const [logger] = useState(() => createLogger(`useSocketIO (${roomPath ? `${roomPath}` : "/"})`));
  const shouldConnect = enabled && (authStatus === "authenticated" || allowUnauthenticated) && (isDocumentVisible || stayConnectedWhenDocumentNotVisible);
  useEffect(() => {
    let accessTokenChanged = false;
    if (socket.io.opts.query?.accessToken !== accessToken) {
      accessTokenChanged = true;
      socket.io.opts.query = {
        ...socket.io.opts.query,
        accessToken
      };
    }
    if (socket.connected) {
      if (!shouldConnect) {
        socket.disconnect();
      } else if (accessTokenChanged) {
        socket.once("disconnect", () => {
          socket.connect();
        });
        socket.disconnect();
      }
    } else {
      if (shouldConnect) {
        socket.connect();
      }
    }
  }, [
    socket,
    shouldConnect,
    accessToken
  ]);
  useWindowEvent("beforeunload", () => {
    socket.disconnect();
  });
  useEffect(() => {
    socket.on("connect", () => {
      logger.logDevOnly("Connected");
      setState({
        isConnected: true,
        error: null,
        connectErrorCount: 0
      });
    });
    socket.on("disconnect", () => {
      logger.logDevOnly("Disconnected");
      setState({
        isConnected: false,
        error: null,
        connectErrorCount: 0
      });
    });
    socket.on("connect_error", (error) => {
      setState((state2) => {
        const newCount = state2.connectErrorCount + 1;
        if (newCount > 2) {
          captureException(new Error("Couldn't establish websocket"), {
            extra: {
              ...error,
              room: roomPath || "/",
              authStatus: authStatusRef.current
            }
          });
        }
        return {
          isConnected: false,
          error,
          connectErrorCount: newCount
        };
      });
    });
    socket.io.on("reconnect", (attempt) => {
      logger.logDevOnly("Reconnected after", attempt, "attempts");
      setState({
        isConnected: true,
        error: null,
        connectErrorCount: 0
      });
    });
    if (getCompatEnv().SOCKETIO_LOG_EVENTS) {
      socket.onAny((event, ...args) => {
        logger.logDevOnly("Event:", event, "Args:", args);
      });
    }
    return () => {
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [
    logger,
    roomPath,
    socket
  ]);
  return {
    socket,
    logger,
    ...state
  };
};

export { useFabricSocketIo };
//# sourceMappingURL=useFabricSocketIo.js.map
